import { AddMediaElementCommand } from './AddMediaElementCommand';
import { VideoEditor } from '../VideoEditor';
import { Element } from '../models/Element';
import { TimelineMode } from './TimelineMode';
import { VideoEditorLogger } from '../../utility/VideoEditorLogger';

/**
 * Command for adding text elements to the timeline with specialized text handling.
 * Extends AddMediaElementCommand to inherit collision detection and track management,
 * while adding text-specific initialization and validation.
 */
export class AddTextElementCommand extends AddMediaElementCommand {
  constructor(
    editor: VideoEditor,
    element: Element,
    targetTrackId?: string,
    mode?: TimelineMode,
  ) {
    super(editor, element, targetTrackId, mode ?? editor.getTimelineMode());
  }

  execute(): boolean {
    VideoEditorLogger.logCommand(this, 'start', {
      elementId: this.element.id,
      elementType: this.element.type,
      targetTrackId: this.targetTrackId,
      mode: this.mode,
    });

    // Validate text element properties
    if (!this.validateTextElement()) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Text element validation failed',
      });
      return false;
    }

    // Call parent execute which handles timeline placement and collision detection
    const success = super.execute();

    if (success) {
      VideoEditorLogger.logCommand(this, 'complete', {
        elementId: this.element.id,
        finalTrackId: this.finalTrackId,
      });
    }

    return success;
  }

  getDescription(): string {
    const trackInfo = this.trackCreated
      ? `new text track`
      : `track ${this.finalTrackId || this.targetTrackId || 'auto'}`;
    return `Add text element to ${trackInfo} (${this.mode} mode)`;
  }

  /**
   * Validates that the text element has required properties
   */
  private validateTextElement(): boolean {
    if (this.element.type !== 'text') {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Element is not a text type',
        actualType: this.element.type,
      });
      return false;
    }

    // Ensure element has a valid duration
    if (this.element.duration === null || this.element.durationSeconds <= 0) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Text element must have a valid duration',
        duration: this.element.duration,
      });
      return false;
    }

    // Ensure element has required text properties
    const requiredProps = ['text', 'font_family', 'font_size'];
    for (const prop of requiredProps) {
      if (!(prop in this.element) || (this.element as any)[prop] === undefined) {
        VideoEditorLogger.logCommand(this, 'error', {
          error: `Missing required text property: ${prop}`,
        });
        return false;
      }
    }

    return true;
  }

}